import { useEffect } from "react";
import { NEXT_LANDMARK, PREV_LANDMARK } from "../constants";

export const useLandmarkNavigation = () => {
  const getLandmarks = (): HTMLElement[] => {
    const nodeList = document.querySelectorAll("[data-landmark]");
    const landmarks = Array.from(nodeList) as HTMLElement[];

    landmarks.sort((a, b) => {
      const aIndex = parseInt(a.getAttribute("data-landmark") || "0", 10);
      const bIndex = parseInt(b.getAttribute("data-landmark") || "0", 10);
      return aIndex - bIndex;
    });
    return landmarks;
  };

  // Individua l'index del landmark in cui si trova attualmente il focus
  const getCurrentLandmarkIndex = (landmarks: HTMLElement[]): number => {
    const active = document.activeElement as HTMLElement | null;
    if (!active) return -1;

    let parent: HTMLElement | null = active;
    // Risale nel DOM finché non trova un elemento con data-landmark
    while (parent && !parent.hasAttribute("data-landmark")) {
      parent = parent.parentElement;
    }
    if (!parent) return -1;

    // Legge il data-landmark per capire che "indice" abbiamo
    const id = parseInt(parent.getAttribute("data-landmark") || "-1", 10);
    return landmarks.findIndex((lm) => {
      const lmId = parseInt(lm.getAttribute("data-landmark") || "-1", 10);
      return lmId === id;
    });
  };

  /*   const getCurrentLandmarkIndex = (landmarks: HTMLElement[]): number => {
    const active = (document.activeElement as HTMLElement) || null;
    if(!active) return -1;

    const allLandmarks = document.querySelectorAll("[data-landmark]");
    for (let i = 0; i < allLandmarks.length; i++){
        const landmark = allLandmarks[i] as HTMLElement;

        if (landmark.contains(active) || landmark === active){
            const id= parseInt(landmark.getAttribute("data-landmark") || "-1", 10);
            return landmarks.findIndex(lm => parseInt(lm.getAttribute("data-landmark") || "-1" , 10) === id);
        }
    }
    return -1;
  }; */

  const getFirstFocusableElement = (
    container: HTMLElement
  ): HTMLElement | null => {
    const focusables = container.querySelectorAll(
      'a[href], button, textarea, input, select, [tabindex]:not([tabindex="-1"])'
    );
    return focusables.length > 0 ? (focusables[0] as HTMLElement) : null;
  };

  const focusLandmarkIndex = (index: number) => {
    const landmarks = getLandmarks();
    if (landmarks.length === 0) return;

    // Evita che l'indice vada fuori range (se non vuoi il "wrap" circolare).
    if (index < 0) index = 0;
    if (index >= landmarks.length) index = landmarks.length - 1;

    const landmark = landmarks[index];
    const focusable = getFirstFocusableElement(landmark);
    if (focusable) {
      focusable.focus();
    } else {
      // Se non ci sono elementi focusabili, volendo puoi rendere il container focusabile con tabIndex e dargli il focus
      landmark.focus?.();
    }
  };

  const focusNextLandmark = () => {
    const landmarks = getLandmarks();
    if (landmarks.length === 0) return;

    const currentIndex = getCurrentLandmarkIndex(landmarks);
    // se -1 → non siamo in alcun landmark, lo tratto come se fossi "prima" del primo
    let newIndex = currentIndex < 0 ? 0 : currentIndex + 1;
    if (newIndex >= landmarks.length) {
      newIndex = landmarks.length - 1;
    }
    focusLandmarkIndex(newIndex);
  };
  const focusPrevLandmark = () => {
    const landmarks = getLandmarks();
    if (landmarks.length === 0) return;

    const currentIndex = getCurrentLandmarkIndex(landmarks);
    let newIndex = currentIndex < 0 ? 0 : currentIndex - 1;
    if (newIndex < 0) {
      newIndex = 0;
    }
    focusLandmarkIndex(newIndex);
  };

  /*  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Evita di intercettare i tasti se l'utente sta digitando in un input/textarea, ecc.
      const active = document.activeElement as HTMLElement | null;
      const isTyping =
        active &&
        (active.tagName === "INPUT" ||
          active.tagName === "TEXTAREA" ||
          active.isContentEditable);

      if (isTyping) {
        return;
      }

      // Normalizziamo il tasto premuto a minuscolo
      const key = e.key.toLowerCase();
      const isShift = e.shiftKey;

      // Controlla combinazioni:
      // "r" → prossimo landmark
      // "Shift + r" → landmark precedente
      // "q" → torna al primo landmark
      if (key === NEXT_LANDMARK && !isShift) {
        e.preventDefault();
        e.stopPropagation();
        focusNextLandmark();
      } else if (key === NEXT_LANDMARK && isShift) {
        e.preventDefault();
        focusPrevLandmark();
      } else if (key === PREV_LANDMARK) {
        e.preventDefault();
        focusLandmarkIndex(0);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []); */

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Evita di intercettare eventi non necessari
      if (e.defaultPrevented) return;

      const active = document.activeElement as HTMLElement | null;
      const isTyping =
        active &&
        (active.tagName === "INPUT" ||
          active.tagName === "TEXTAREA" ||
          active.isContentEditable);

      if (isTyping) return;

      if (!e.key) return;
      const key = e.key.toLowerCase();
      const isShift = e.shiftKey;

      if (key === NEXT_LANDMARK && !isShift) {
        e.preventDefault();
        e.stopImmediatePropagation(); // Blocca ulteriormente l'evento
        focusNextLandmark();
      } else if (key === NEXT_LANDMARK && isShift) {
        e.preventDefault();
        e.stopImmediatePropagation();
        focusPrevLandmark();
      } else if (key === PREV_LANDMARK) {
        e.preventDefault();
        e.stopImmediatePropagation();
        focusLandmarkIndex(0);
      }
    };

    window.addEventListener("keydown", handleKeyDown, true); // Fase di cattura
    return () => {
      window.removeEventListener("keydown", handleKeyDown, true);
    };
  }, []);
};
